/* eslint-disable react/prop-types, react/destructuring-assignment */
import { Toggle } from '@augmentt-dev/ui-components'
import { Formik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { Button, Col, Form, Row, Tab, Tabs } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import PerfectScrollbar from 'react-perfect-scrollbar'
import PhoneInput from 'react-phone-input-2'
import Select, { components, createFilter } from 'react-select'
import { multiSelectStyle, selectStyle } from '../../../../../../../_augmentt/utils/styles'
import ScrollMenu from '../../../../../../partials/content/ScrollMenu'
import { getFinalValuesFromTaskParameterFunction, getFormikSchema } from '../../../config'
import {
  checkSelection,
  cloneChildComponents,
  formatDomainOptions,
  formatEmployeeOptions,
  Option,
} from '../../../utils/helpers'

function AddUser(props) {
  const { children, ...rest } = props
  const {
    intl,
    show,
    action,
    taskParameters,
    setTaskParameters,
    initialValues,
    allAugmenttEmployees,
    allLicensedEmployees,
    onActionSubmit,
    selectedEmployeeReference,
    setShowFindEmployeeModal,
    selectedCustomer,
    selectedEmployee,
    setDisableSubmit,
    setActionData,
    actionData,
    setCompletedActions,
  } = rest

  const formikRef = useRef() // Used for error handling in the modal footer
  const groupsRef = useRef() // References the group selector. Used to close the menu using handleClickOutside

  // Local state
  const [employeeOptions, setEmployeeOptions] = useState([])
  const [domainOptions, setDomains] = useState([])
  const [showGroupsMenu, setShowGroupsMenu] = useState(false)
  const [groupOptions, setGroupOptions] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])

  const [licenses, setLicenses] = useState([])
  const [licensesToAdd, setLicensesToAdd] = useState([])
  const [activeKey, setActiveKey] = useState('add-employee')
  const [newEmployee, setNewEmployee] = useState()
  const [phone, setPhone] = useState()
  const [schema, setSchema] = useState() // Formik schema

  const [isResettingPassword, setResettingPassword] = useState(true)
  const [isAutoGeneratingPassword, setAutoGeneratingPassword] = useState(true)
  const [isPasswordChangeRequired, setPasswordChangeRequired] = useState(true)
  const [isSendingPasswordToMe, setSendingPasswordToMe] = useState(true)
  const [sendEmailTo, setSendEmailTo] = useState()

  // Success action ids
  const EMPLOYEE_CREATED = 'ENGAGE.EMPLOYEE_CREATED'
  const ACCOUNT_CREATED = 'ENGAGE.ACCOUNT_CREATED'
  const LICENSES_ASSIGNED = 'ENGAGE.LICENSES_ASSIGNED'
  const ADDED_TO_GROUP = 'ENGAGE.ADDED_TO_GROUP'

  // Handles initial render of component
  useEffect(() => {
    if (show) {
      if (selectedEmployee) {
        setActiveKey('account-details')
        if (selectedEmployee.employeeIdentifier) {
          // Existing gws account, default reset password to false
          setResettingPassword(false)
          setPasswordChangeRequired(false)
        } else {
          // No gws account, so make reset password required
          setResettingPassword(true)
          setPasswordChangeRequired(true)
        }
      } else {
        // No employee, so force reset password
        setResettingPassword(true)
        setPasswordChangeRequired(true)
      }

      formatEmployeeOptions(allAugmenttEmployees, intl, setEmployeeOptions)
      formatDomainOptions(allLicensedEmployees, setDomains, {
        adminDomainsOnly: false,
      })
      // eslint-disable-next-line no-use-before-define
      formatGroupOptions()
      // eslint-disable-next-line no-use-before-define
      getAllLicenses()
    }

    // Adding click handler to control multiselect menu visibility of the group selector
    // eslint-disable-next-line no-use-before-define
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      // eslint-disable-next-line no-use-before-define
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles fetching the formik schema
  useEffect(() => {
    // Formik schema
    const schemaOptions = {
      isAutoGeneratingPassword,
      activeKey, // The current tab of the formik
    }

    setSchema(getFormikSchema(action, intl, schemaOptions))
  }, [activeKey, isAutoGeneratingPassword]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles disabling submit button in the footer child component
  useEffect(() => {
    if (
      (activeKey === 'account-details' &&
        selectedEmployee?.employeeIdentifier === undefined &&
        (!actionData?.username ||
          !actionData?.domain ||
          (!isAutoGeneratingPassword && !actionData?.newPassword) ||
          (!isSendingPasswordToMe && isResettingPassword && !sendEmailTo))) ||
      (activeKey === 'add-employee' &&
        (!actionData?.firstName || !actionData?.lastName || !actionData?.department || !actionData?.jobTitle)) ||
      (activeKey === 'assign-license' && !licensesToAdd?.length)
    ) {
      setDisableSubmit(true)
    } else {
      setDisableSubmit(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeKey,
    actionData,
    isAutoGeneratingPassword,
    isSendingPasswordToMe,
    isResettingPassword,
    sendEmailTo,
    licensesToAdd,
  ])

  // Handles newEmployee change -> sets newEmployee in the actionData to be used in the header, as well as newEmployeeName for the success body
  useEffect(() => {
    if (newEmployee) {
      const newEmployeeData = {
        newEmployee, // Used in the header
        newEmployeeName: `${newEmployee.firstName} ${newEmployee.lastName}`, // Used in the success body
      }

      setActionData({ ...actionData, ...newEmployeeData })
    }
  }, [newEmployee]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles licensesToAdd change -> sets licensesAssigned in the actionData to be used in the success body
  useEffect(() => {
    if (licensesToAdd?.length) {
      setActionData({ ...actionData, licensesAssigned: licensesToAdd }) // Used in the success body
    }
  }, [licensesToAdd]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles firing off the task once the last required data field has been added to actionDatas
  useEffect(() => {
    // Submit the task if actionData has isResettingPassword as a key. This is the final state setter and is used as a reference for when to fire off the task
    // eslint-disable-next-line no-prototype-builtins
    if (actionData?.hasOwnProperty('isResettingPassword')) {
      // eslint-disable-next-line no-use-before-define
      submitTheTask()
    }
  }, [actionData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles setting the base license
  useEffect(() => {
    if (licenses?.length) {
      // eslint-disable-next-line no-restricted-syntax
      for (const license of licenses) {
        if (license?.isPrimary) {
          // eslint-disable-next-line no-shadow
          setLicensesToAdd((licensesToAdd) => [...licensesToAdd, license])
          break
        }
      }
    }
  }, [licenses])

  // Retrieves all GWS licenses from the employee integration info
  const getAllLicenses = async () => {
    const licenseMap = new Map() // {key: licenseId, value: licenseObject}

    // Get the base (default) GWS license from any employees
    // eslint-disable-next-line no-restricted-syntax
    for (const employee of allLicensedEmployees) {
      // eslint-disable-next-line no-restricted-syntax
      for (const license of employee.licenseType) {
        if (licenseMap.has(license.id)) {
          continue // eslint-disable-line no-continue
        } else {
          // Add the license to the map
          licenseMap.set(license.id, license)
        }
      }
    }

    setLicenses(Array.from(licenseMap, ([, value]) => ({ ...value }))) // Convert map to array of objects
  }

  // Updates a license select checkbox
  const updateLicenses = (licenseId, checked) => {
    const selectedLicenses = [...licensesToAdd]

    // License was selected
    if (licenseId && checked) {
      selectedLicenses.push(licenses.find((license) => license?.id === licenseId))
    } else if (licenseId && !checked) {
      // License was unselected, ensure it is no longer in licensesToAdd (it may not have ever been there)
      const indexToRemove = selectedLicenses.findIndex((license) => license.id === licenseId)
      selectedLicenses.splice(indexToRemove, 1)
    }

    setLicensesToAdd(selectedLicenses)
  }

  // Format options in group selector
  const formatGroupOptions = () => {
    let options = []

    // Format customer groups
    // eslint-disable-next-line no-restricted-syntax
    for (const employee of allLicensedEmployees) {
      const employeeGroups = employee.groups || []
      // eslint-disable-next-line no-restricted-syntax
      for (const groups of employeeGroups) {
        options.push({
          value: groups.id,
          label: groups.name,
        })
      }
    }

    options = options.filter((v, i, a) => a.findIndex((t) => t.label === v.label) === i)
    // Sort by name
    options.sort((a, b) => a.label.localeCompare(b.label))
    setGroupOptions(options)
  }

  // Fires off task to integration manager
  const submitTheTask = () => {
    // Fire off task
    let finalTaskParameters = taskParameters

    // Form task parameter fields for taskParameters callback
    const taskParameterFields = {
      selectedEmployee: selectedEmployee || newEmployee, // If adding license to existing employee, otherwise use newEmployee
      actionData,
    }

    // Task parameters should be a callback function here that was set in Data Loader
    if (typeof finalTaskParameters === 'function') {
      try {
        finalTaskParameters = getFinalValuesFromTaskParameterFunction(action, finalTaskParameters, taskParameterFields)
      } catch (error) {
        console.error(error)
      }
      setTaskParameters(finalTaskParameters)
    } else {
      console.error('Error - taskParameters should be a function')
    }

    // Submit the task
    onActionSubmit(action, finalTaskParameters)
  }

  // onSubmit callback for footer
  const next = (values) => {
    if (activeKey === 'add-employee') {
      setActiveKey('account-details')
      setNewEmployee(values)
    } else if (activeKey === 'account-details') {
      setActiveKey('assign-license')
      setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, ACCOUNT_CREATED]) // Add the account created action id to the completed actions state
      setActionData({
        ...actionData,
        newEmployeeEmail: `${values?.username}@${values?.domain?.value}`,
      }) // Add newEmployeeEmail to actionData to use within the success body
    } else if (activeKey === 'assign-license') {
      setActiveKey('review')
    } else {
      // Check if a new employee was created, if so add it as a completed action
      const newCompletedActions = []
      const newActionData = {}

      if (newEmployee) {
        newCompletedActions.push(EMPLOYEE_CREATED)
      }

      // Add the values of the new employee to the actionData
      if (values) {
        newActionData.values = values
      }

      // Set licenseIds in actionData for task parameters, as well as the completed actionId
      if (licensesToAdd?.length) {
        newCompletedActions.push(LICENSES_ASSIGNED)
        newActionData.licenseIdsToAdd = licensesToAdd.map((license) => license.id)
      }

      // Set the groupIdsToAdd in the actionData for task parameters, as well as the completed actionId
      if (selectedGroups?.length) {
        newCompletedActions.push(ADDED_TO_GROUP)

        // Format groups for actionData to be used in the success body
        const groups = []
        const groupIds = []
        // eslint-disable-next-line no-restricted-syntax
        for (const group of selectedGroups) {
          groupIds.push(group.value)
          groups.push({ name: group.label })
        }

        newActionData.groupIdsToAdd = groupIds // For task parameters
        newActionData.groups = groups // for success view
      }

      // Add isResettingPassword to actionData for task parameters
      newActionData.isResettingPassword = isResettingPassword

      // Set the new completed actions and action data
      setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, ...newCompletedActions])
      setActionData({ ...actionData, ...newActionData })
      // Note: This will trigger the actionData useEffect to fire off the task to the integration manager
    }
  }

  // Component override for react-select (future improvement is to try to get this modularized in helpers.js)
  // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
  function ValueContainer({ children, getValue, ...props }) {
    const { length } = getValue()
    return (
      <components.ValueContainer {...props}>
        {length === 1 && (
          <>
            <span className="multi-select-value">{props.selectProps.value[0].label}</span>
            <ClearIndicator {...props} />
          </>
        )}
        {length > 1 && (
          <>
            <span className="multi-select-value">{length}</span>
            <ClearIndicator {...props} />
            <components.Placeholder {...props} className="multi-select-label">
              {props.selectProps.placeholder}
            </components.Placeholder>
          </>
        )}
      </components.ValueContainer>
    )
  }

  // Clear button component override
  // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
  function ClearIndicator({ innerRef, children, ...props }) {
    const onClear = (e) => {
      if (props.selectProps?.name === 'groups') {
        setSelectedGroups([])
      }

      e.stopPropagation()
      e.preventDefault()
    }

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div ref={innerRef} className="multi-select-clear" onClick={onClear}>
        <components.ClearIndicator {...props}>
          <span className="icon-xs flaticon2-cross p-2" />
        </components.ClearIndicator>
      </div>
    )
  }

  // Click handler for group multi-select menu
  const handleClick = (target) => {
    if (target === 'groups') {
      if (showGroupsMenu) {
        setShowGroupsMenu(false)
        if (groupsRef.current.select.inputRef) {
          groupsRef.current.select.inputRef.blur()
        }
      } else {
        setShowGroupsMenu(true)
      }
    }
  }

  // Outside click handler for hiding group multi-select menu (workaround due to closeMenuOnSelect being disabled)
  const handleClickOutside = (event) => {
    if (
      groupsRef.current &&
      !groupsRef.current.select.controlRef.contains(event.target) &&
      !groupsRef.current.select?.focusedOptionRef?.contains(event.target)
    ) {
      setShowGroupsMenu(false)
      if (groupsRef.current.select.inputRef) {
        groupsRef.current.select.inputRef.blur()
      }
    }
  }

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={schema || undefined}
      initialValues={initialValues || undefined}
      initialErrors={schema?.isValidSync(initialValues) ? schema.isValidSync(initialValues) : false}
      onSubmit={(values) => next(values)}
    >
      {({ handleSubmit, setFieldValue, setFieldTouched, values, errors, touched }) => (
        <PerfectScrollbar options={{ wheelSpeed: 2, wheelPropagation: false }} className="paginator-margin-bottom">
          <Form>
            <Tabs activeKey={activeKey} onSelect={(key) => setActiveKey(key)} className="nav-fill engage-tabs">
              {!selectedEmployee && (
                <Tab
                  eventKey="add-employee"
                  tabClassName="engage-tab"
                  title={
                    <p>
                      <FormattedMessage id="ADMINISTRATION.ADD_EMPLOYEE" />
                    </p>
                  }
                >
                  <div className="section">
                    <Row className="justify-content-center align-items-center">
                      <Row style={{ width: '100%' }}>
                        <Form.Group as={Col} md="11" controlId="employeeDepartment">
                          <Form.Label className="font-bigger font-bolder required">
                            <FormattedMessage id="FORM.DEPARTMENT" />
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="department"
                            placeholder={intl.formatMessage({
                              id: 'PLACEHOLDER.ENTER_DEPARTMENT',
                            })}
                            onChange={(e) => {
                              setActionData({
                                ...actionData,
                                department: e.target.value,
                              })
                            }}
                            onBlur={(e) => {
                              const val = (e.target.value || '').replace(/\s+/gi, ' ').trim()
                              setFieldValue('department', val)
                              setFieldTouched('department', true, false)
                            }}
                            onKeyPress={(event) => {
                              if (event.key === ' ' && !event.target.value.length) {
                                event.preventDefault()
                              }
                            }}
                            isInvalid={touched.department && errors.department}
                            isValid={touched.department && !errors.department}
                            className="ms-3"
                          />
                          <Form.Control.Feedback className="feedback-control" type="invalid">
                            {touched.department && errors.department}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="11" controlId="employeeJobTitle" className="mt-3">
                          <Form.Label className="font-bigger font-bolder required">
                            <FormattedMessage id="FORM.JOB_TITLE" />
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="jobTitle"
                            placeholder={intl.formatMessage({
                              id: 'PLACEHOLDER.ENTER_JOB_TITLE',
                            })}
                            onChange={(e) => {
                              setActionData({
                                ...actionData,
                                jobTitle: e.target.value,
                              })
                            }}
                            onBlur={(e) => {
                              const val = (e.target.value || '').replace(/\s+/gi, ' ').trim()
                              setFieldValue('jobTitle', val)
                              setFieldTouched('jobTitle', true, false)
                            }}
                            onKeyPress={(event) => {
                              if (event.key === ' ' && !event.target.value.length) {
                                event.preventDefault()
                              }
                            }}
                            isInvalid={touched.jobTitle && !!errors.jobTitle}
                            isValid={touched.jobTitle && !errors.jobTitle}
                            className="ms-3"
                          />
                          <Form.Control.Feedback className="feedback-control" type="invalid">
                            {touched.jobTitle && errors.jobTitle}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="5" controlId="employeeName" className="mt-3">
                          <Form.Label className="font-bigger font-bolder required">
                            <FormattedMessage id="FORM.NAME" />
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="firstName"
                            placeholder={intl.formatMessage({
                              id: 'PLACEHOLDER.ENTER_FIRST_NAME',
                            })}
                            onChange={(e) => {
                              setActionData({
                                ...actionData,
                                firstName: e.target.value,
                              })
                            }}
                            onBlur={(e) => {
                              const val = (e.target.value || '').replace(/\s+/gi, ' ').trim()
                              setFieldValue('firstName', val)
                              setFieldTouched('firstName', true, false)
                            }}
                            onKeyPress={(event) => {
                              if (event.key === ' ' && !event.target.value.length) {
                                event.preventDefault()
                              }
                            }}
                            isInvalid={touched.firstName && !!errors.firstName}
                            isValid={touched.firstName && !errors.firstName}
                            className="ms-3"
                          />
                          <Form.Control.Feedback className="feedback-control" type="invalid">
                            {touched.firstName && errors.firstName}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="6" controlId="employeeLastName" className="mt-3">
                          <Form.Control
                            type="text"
                            name="lastName"
                            placeholder={intl.formatMessage({
                              id: 'PLACEHOLDER.ENTER_LAST_NAME',
                            })}
                            style={{ marginTop: '27px' }}
                            onChange={(e) => {
                              setActionData({
                                ...actionData,
                                lastName: e.target.value,
                              })
                            }}
                            onBlur={(e) => {
                              const val = (e.target.value || '').replace(/\s+/gi, ' ').trim()
                              setFieldValue('lastName', val)
                              setFieldTouched('lastName', true, false)
                            }}
                            onKeyPress={(event) => {
                              if (event.key === ' ' && !event.target.value.length) {
                                event.preventDefault()
                              }
                            }}
                            isInvalid={touched.lastName && !!errors.lastName}
                            isValid={touched.lastName && !errors.lastName}
                            className="ms-3"
                          />
                          <Form.Control.Feedback className="feedback-control" type="invalid">
                            {touched.lastName && errors.lastName}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} md="11" controlId="phone" className="mt-3">
                          <Form.Label className="font-bigger font-bolder">
                            <FormattedMessage id="FORM.PHONE" />
                          </Form.Label>
                          <PhoneInput
                            name="phone"
                            preferredCountries={['ca', 'us', 'gb']}
                            country="ca"
                            value={values?.phone}
                            // eslint-disable-next-line no-shadow
                            onChange={(phone) => {
                              setPhone(phone)
                              setActionData({
                                ...actionData,
                                phoneNumber: phone,
                              })
                            }}
                            placeholder={intl.formatMessage({
                              id: 'PLACEHOLDER.ENTER_PHONE',
                            })}
                            disableCountryCode={!phone}
                            containerClass="ms-3"
                          />
                        </Form.Group>
                      </Row>
                    </Row>
                  </div>
                </Tab>
              )}
              <Tab
                eventKey="account-details"
                tabClassName="engage-tab"
                title={
                  <p>
                    <FormattedMessage id="ENGAGE.ACCOUNT_DETAILS_C" />
                  </p>
                }
                disabled={activeKey === 'add-employee'}
              >
                <Row>
                  <Col sm="12" md="12" lg="12">
                    <p className="font-bolder font-lg mb-3">
                      <FormattedMessage id="USERS.USERNAME" />
                    </p>

                    {selectedEmployee && selectedEmployee.employeeIdentifier && (
                      <p className="font-bolder ms-4 mb-3">{selectedEmployee.email}</p>
                    )}

                    {(!selectedEmployee || !selectedEmployee.employeeIdentifier) && (
                      <div className="mb-4">
                        <Form.Group id="username" style={{ display: 'inline' }}>
                          <p className="ps-3 mb-3">
                            <FormattedMessage id="ENGAGE.CREATE_USERNAME" />
                          </p>
                          <Col sm="6" md="6" lg="6" className="inline">
                            <Form.Control
                              type="text"
                              className="font-bolder"
                              name="username"
                              placeholder={intl.formatMessage({
                                id: 'PLACEHOLDER.ENTER_USERNAME',
                              })}
                              onChange={(e) => {
                                setActionData({
                                  ...actionData,
                                  username: e.target.value,
                                })
                              }}
                              onBlur={(e) => {
                                const val = (e.target.value || '').replace(/\s+/gi, ' ').trim()
                                setFieldValue('username', val)
                                setFieldTouched('username', true, false)
                              }}
                            />
                            <Form.Control.Feedback className="feedback-control" type="invalid">
                              {touched.username && errors.username}
                            </Form.Control.Feedback>
                          </Col>
                        </Form.Group>

                        <span className="font-bolder p2 mt-2" style={{ position: 'absolute' }}>
                          @
                        </span>

                        <Form.Group id="domain" className="engage-domain-select">
                          <Col sm="5" md="5" lg="5" className="inline">
                            <Select
                              id="domainSelect"
                              name="domain"
                              styles={selectStyle}
                              options={domainOptions}
                              value={values.domain}
                              onChange={(value) => {
                                setActionData({
                                  ...actionData,
                                  domain: value,
                                })
                                setFieldValue('domain', value)
                              }}
                              getOptionValue={(value) => value}
                              onBlur={() => setFieldTouched('domain', true)}
                              placeholder={intl.formatMessage({
                                id: 'PLACEHOLDER.SELECT_DOMAIN',
                              })}
                              components={{ MenuList: ScrollMenu }}
                              filterOption={createFilter({
                                matchFrom: 'any',
                                stringify: (option) => `${option.label}`,
                              })}
                            />
                          </Col>
                        </Form.Group>
                      </div>
                    )}

                    <br />

                    {/* Password Settings */}
                    <p className="font-bolder font-lg mb-3">
                      <FormattedMessage id="ENGAGE.PASSWORD_SETTINGS" />
                    </p>

                    <div className="ms-4">
                      <Form.Group id="resetPassword" className="mb-3">
                        <Form.Check
                          custom
                          type="checkbox"
                          id="resetPasswordCheckbox"
                          disabled={isPasswordChangeRequired}
                          checked={isResettingPassword}
                          label={intl.formatMessage({
                            id: 'ENGAGE.RESET_PASSWORD',
                          })}
                          onClick={() => setResettingPassword(!isResettingPassword)}
                        />
                      </Form.Group>

                      {isResettingPassword && (
                        <>
                          <div id="passwordToggle" className="flex mb-2">
                            <p
                              className="pe-3"
                              style={{
                                color: isAutoGeneratingPassword ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                              }}
                            >
                              <FormattedMessage id="ENGAGE.AUTO_GENERATE_PASSWORD" />
                            </p>
                            <Toggle
                              onChange={() => setAutoGeneratingPassword(!isAutoGeneratingPassword)}
                              checked={!isAutoGeneratingPassword}
                              className="switch"
                              labelText=""
                              ariaLabel="password-toggle"
                            />
                            <p
                              className="ps-3"
                              style={{
                                color: !isAutoGeneratingPassword ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                              }}
                            >
                              <FormattedMessage id="ENGAGE.MANUALLY_CREATE_PASSWORD" />
                            </p>
                          </div>

                          {!isAutoGeneratingPassword && (
                            <Form.Group id="manualPassword">
                              <Form.Control
                                type="text"
                                className="inline"
                                name="newPassword"
                                placeholder={intl.formatMessage({
                                  id: 'PLACEHOLDER.ENTER_PASSWORD',
                                })}
                                onChange={(e) => {
                                  setActionData({
                                    ...actionData,
                                    newPassword: e.target.value,
                                  })
                                }}
                                onBlur={(e) => {
                                  const val = (e.target.value || '').replace(/\s+/gi, ' ').trim()
                                  setFieldValue('newPassword', val)
                                  setFieldTouched('newPassword', true, false)
                                }}
                              />
                              <Form.Control.Feedback
                                style={{ width: '75%' }}
                                className="feedback-control"
                                type="invalid"
                              >
                                {touched.newPassword && errors.newPassword}
                              </Form.Control.Feedback>
                            </Form.Group>
                          )}

                          <p className="font-color-2">
                            <FormattedMessage id="ENGAGE.USER_REQUIRED_TO_CHANGE_PASSWORD" />
                          </p>

                          <p className="mt-4 mb-4 font-bolder">
                            {intl.formatMessage({
                              id: 'ENGAGE.SEND_PASSWORD_ON_COMPLETE',
                            })}
                          </p>

                          <>
                            <div id="sendPasswordToggle" className="flex">
                              <p
                                className="pe-3"
                                style={{
                                  color: isSendingPasswordToMe ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                                }}
                              >
                                <FormattedMessage id="ENGAGE.TO_ME" />
                              </p>
                              <Toggle
                                onChange={() => setSendingPasswordToMe(!isSendingPasswordToMe)}
                                checked={!isSendingPasswordToMe}
                                className="switch"
                                labelText=""
                                ariaLabel="send-password-toggle"
                              />
                              <p
                                className="ps-3"
                                style={{
                                  color: !isSendingPasswordToMe ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                                }}
                              >
                                {intl.formatMessage(
                                  {
                                    id: 'ENGAGE.TO_AN_EMPLOYEE_AT_TENANT',
                                  },
                                  {
                                    tenant: selectedCustomer.customer_name,
                                  },
                                )}
                              </p>
                            </div>

                            {isSendingPasswordToMe && (
                              <Form.Group id="email">
                                <Form.Control
                                  type="text"
                                  className="inline font-bolder"
                                  name="email"
                                  placeholder={intl.formatMessage({
                                    id: 'PLACEHOLDER.ENTER_EMAIL',
                                  })}
                                  disabled
                                  value={values?.email}
                                  onChange={(e) => {
                                    setActionData({
                                      ...actionData,
                                      email: e.target.value,
                                    })
                                  }}
                                  onBlur={(e) => {
                                    const val = (e.target.value || '').replace(/\s+/gi, ' ').trim()
                                    setFieldValue('email', val)
                                    setFieldTouched('email', true, false)
                                  }}
                                />
                              </Form.Group>
                            )}

                            {!isSendingPasswordToMe && (
                              <Select
                                id="employeeSelect"
                                ref={selectedEmployeeReference}
                                name="employee"
                                className="inline"
                                styles={selectStyle}
                                options={employeeOptions}
                                onChange={(value) => {
                                  setSendEmailTo(value)
                                  setActionData({
                                    ...actionData,
                                    employee: value,
                                  })
                                  setFieldValue('employee', value)
                                  checkSelection(value, setShowFindEmployeeModal, selectedEmployeeReference)
                                }}
                                getOptionValue={(value) => value}
                                isClearable
                                onBlur={() => setFieldTouched('employee', true)}
                                placeholder={intl.formatMessage({
                                  id: 'PLACEHOLDER.SELECT_EMPLOYEE',
                                })}
                                components={{
                                  MenuList: ScrollMenu,
                                  // eslint-disable-next-line no-shadow, react/no-unstable-nested-components
                                  Option: (props) => <Option {...props} optionType="employee" />,
                                }}
                                filterOption={createFilter({
                                  matchFrom: 'any',
                                  stringify: (option) => `${option.label}`,
                                })}
                              />
                            )}
                          </>
                        </>
                      )}
                    </div>

                    <br />

                    {/* Groups */}
                    <p className="font-bolder font-lg mb-3">
                      <FormattedMessage id="ENGAGE.GROUPS" />
                    </p>

                    <div className="ps-3">
                      <p className="mb-3">
                        <FormattedMessage id="ENGAGE.ADD_TO_GROUPS" />
                      </p>
                      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                      <div className="select-container" onClick={() => handleClick('groups')}>
                        <Select
                          isMulti
                          id="groupsSelect"
                          name="groups"
                          ref={groupsRef}
                          menuIsOpen={showGroupsMenu}
                          autoBlur
                          className="inline"
                          classNamePrefix="multi-select"
                          styles={multiSelectStyle}
                          options={groupOptions}
                          value={selectedGroups}
                          closeMenuOnSelect={false}
                          onChange={(groups) => {
                            if (groups && groups.length) {
                              setSelectedGroups(groups)
                            } else {
                              setSelectedGroups([])
                            }
                          }}
                          hideSelectedOptions={false}
                          isClearable={false}
                          placeholder={
                            selectedGroups?.length
                              ? intl.formatMessage({ id: 'ENGAGE.GROUPS' })
                              : intl.formatMessage({
                                  id: 'ENGAGE.SELECT_GROUPS',
                                })
                          }
                          components={
                            selectedGroups?.length
                              ? {
                                  MenuList: ScrollMenu,
                                  ClearIndicator,
                                  Option,
                                  ValueContainer,
                                }
                              : { MenuList: ScrollMenu, Option }
                          }
                          filterOption={createFilter({
                            matchFrom: 'any',
                            stringify: (option) => `${option.label}`,
                          })}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </Tab>
              <Tab
                eventKey="assign-license"
                tabClassName="engage-tab"
                title={
                  <p>
                    <FormattedMessage id="ADMINISTRATION.ASSIGN_LICENSE_C" />
                  </p>
                }
                disabled={activeKey === 'add-employee' || activeKey === 'account-details'}
              >
                <Row>
                  <Col sm="12" md="12" lg="12">
                    <p className="font-bolder font-lg mb-4">
                      <FormattedMessage id="ENGAGE.ASSIGN_LICENSES" />
                    </p>

                    <Form.Group id="licenses" className="mb-0 ms-4">
                      {licenses.map((license) => (
                        <Form.Check
                          custom
                          type="checkbox"
                          id={license?.id}
                          key={license?.id}
                          defaultChecked={license?.isPrimary}
                          label={<span className="font-bolder">{license?.displayName}</span>}
                          onClick={(e) => {
                            updateLicenses(e.target.id, e.target.checked)
                          }}
                        />
                      ))}
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>
              <Tab
                eventKey="review"
                tabClassName="engage-tab"
                title={
                  <p>
                    <FormattedMessage id="ENGAGE.REVIEW_AND_FINISH" />
                  </p>
                }
                disabled={activeKey !== 'review'}
              >
                <Row>
                  <Col sm="12" md="12" lg="12">
                    <p className="font-bolder font-lg mb-2">
                      <FormattedMessage id="ENGAGE.ACCOUNT_DETAILS_C" />
                    </p>
                    <p className="m0">
                      {selectedEmployee ? selectedEmployee.email : `${values?.username}@${values?.domain?.value}`}
                    </p>
                    <Button
                      variant="link"
                      className="mb-4 ps-0 pe-0"
                      onClick={() => {
                        setActiveKey('account-details')
                      }}
                    >
                      <span>
                        <FormattedMessage id="GENERAL.EDIT" />
                      </span>
                    </Button>
                    {isResettingPassword && (
                      <>
                        <p className="font-bolder font-lg mb-2">
                          <FormattedMessage id="ENGAGE.PASSWORD_SETTINGS" />
                        </p>
                        {isAutoGeneratingPassword && (
                          <p className="m0">
                            <FormattedMessage id="ENGAGE.AUTO_GENERATE" />
                          </p>
                        )}
                        {isPasswordChangeRequired && (
                          <p className="m0">
                            <FormattedMessage id="ENGAGE.REQUIRE_USER_TO_CHANGE_PASSWORD" />
                          </p>
                        )}
                        <Button
                          variant="link"
                          className="mb-4 ps-0 pe-0"
                          onClick={() => setActiveKey('account-details')}
                        >
                          <span>
                            <FormattedMessage id="GENERAL.EDIT" />
                          </span>
                        </Button>
                      </>
                    )}
                    <p className="font-bolder font-lg mb-2">
                      <FormattedMessage id="ENGAGE.LICENSES_TO_BE_ASSIGNED" />
                    </p>
                    {licensesToAdd?.length &&
                      licensesToAdd.map((license, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <p key={index} className="mb-0">
                          {license.displayName}
                        </p>
                      ))}
                    <Button variant="link" className="mb-4 ps-0 pe-0" onClick={() => setActiveKey('assign-license')}>
                      <span>
                        <FormattedMessage id="GENERAL.EDIT" />
                      </span>
                    </Button>
                  </Col>
                </Row>
              </Tab>
            </Tabs>

            {/* Footer Child */}
            {cloneChildComponents(
              {
                ...rest,
                handleSubmit,
                values,
                formikRef,
              },
              children,
            )}
          </Form>
        </PerfectScrollbar>
      )}
    </Formik>
  )
}

export default AddUser
